import { StyleSheet, Document, Page, Text, View, Image } from '@react-pdf/renderer'
import React from 'react'
import HeaderOfPDFFashion from './Header'
import FooterOfPDFFashion from './Footer'
import { SectionTitle } from '../SectionTitle'
import TableOfPDF from '../Table'
import type { FashionOpPdfData } from './fashionOpPdfTypes'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 78,
    paddingBottom: 80,
    paddingHorizontal: 10,
  },
  section: {
    marginBottom: 6,
    paddingBottom: 4,
    borderBottom: '1px solid #000',
  },
  inlineFieldsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 14,
    rowGap: 1,
    marginVertical: 4,
  },
  inlineField: {
    fontSize: 9,
    color: '#000',
    lineHeight: 1.3,
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 3,
    fontSize: 9,
    color: '#000',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  inlineLabel: {
    fontWeight: 'bold',
    fontSize: 9,
    color: '#000',
  },
  value: {
    marginBottom: 4,
    fontSize: 10,
    color: '#000',
    lineHeight: 1.3,
  },
  textBlock: {
    fontSize: 9,
    color: '#000',
    lineHeight: 1.3,
    marginBottom: 3,
  },
  noteRow: {
    marginBottom: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    backgroundColor: '#f5f5f5',
    border: '1px solid #000',
    borderRadius: 3,
  },
  noteSector: {
    fontSize: 9,
    fontWeight: 'bold',
    lineHeight: 1.3,
    marginBottom: 3,
  },
  noteText: {
    fontSize: 9,
    lineHeight: 1.35,
  },
  noteImagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
    gap: 4,
  },
  noteImage: {
    width: 100,
    height: 100,
    objectFit: 'contain',
    borderRadius: 2,
    border: '1px solid #ccc',
  },
  productImageSection: {
    marginBottom: 6,
    paddingBottom: 4,
    borderBottom: '1px solid #000',
    alignItems: 'center',
  },
  productImage: {
    width: 160,
    height: 160,
    objectFit: 'contain',
  },
  fillerTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    minHeight: 28,
  },
  fillerTableCell: {
    fontSize: 8,
    lineHeight: 1.3,
    textAlign: 'left',
    padding: 4,
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#eee',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
})

interface BodyOfPDFFashionType {
  data?: FashionOpPdfData | null
  qrCodeDataUrl?: string
}

const formatQuantity = (value: number): string => {
  if (!Number.isFinite(value)) return '—'
  const rounded = Math.round(value * 1000) / 1000
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(3).replace(/\.?0+$/, '')
}

const BodyOfPDFFashion: React.FC<BodyOfPDFFashionType> = ({ data, qrCodeDataUrl }) => {
  const pushFields = (): { label: string; subLabel?: string; value: string }[] => {
    const fields: { label: string; subLabel?: string; value: string }[] = []

    if (data?.fashionProduct?.name) {
      fields.push({ label: 'Produto', value: data.fashionProduct.name })
    }
    if (data?.color?.name) {
      fields.push({ label: 'Cor', value: data.color.name })
    }
    fields.push({
      label: 'Coleção',
      value: data?.fashionProduct?.collectionName?.trim() || '—',
    })
    fields.push({
      label: 'Marca',
      value: data?.fashionProduct?.collectionBrand?.trim() || '—',
    })
    if (data?.fashionProduct?.familyName?.trim()) {
      fields.push({ label: 'Família', value: data.fashionProduct.familyName.trim() })
    }
    if (data?.fashionProduct?.episodeName?.trim()) {
      fields.push({ label: 'Episódio', value: data.fashionProduct.episodeName.trim() })
    }
    if (data?.fashionProduct?.referenceProduct?.trim()) {
      fields.push({ label: 'Produto referência', value: data.fashionProduct.referenceProduct.trim() })
    }
    if (data?.totalItems) {
      fields.push({ label: 'Quantidade de Itens', value: String(data.totalItems) })
    }
    const pedidosStr = (data?.orders ?? []).map((o) => `PED-${o.fashionOrderId}`).join(', ')
    if (pedidosStr) {
      fields.push({ label: 'Pedido(s)', value: pedidosStr })
    }
    if (data?.cutter?.name) {
      fields.push({ label: 'Cortador', value: data.cutter.name })
    }
    if (data?.dressmaker?.name) {
      fields.push({ label: 'Costureira', value: data.dressmaker.name })
    }

    return fields
  }

  const sizeColumns = data?.sizeColumns ?? []

  // Grade unificada: cor + totais somados + linhas de retorno
  const totalPerSize: Record<string, number> = {}
  sizeColumns.forEach((size) => {
    totalPerSize[size] = (data?.orders ?? []).reduce((sum, o) => sum + (o.sizeQuantities[size] ?? 0), 0)
  })
  const extraManualColumns = 3
  const gradeSizeFlex = 0.7
  const gradeExtraFlex = 0.7
  const gradeCorFlex = 2.2

  const gradeHeaders = ['Cor', ...sizeColumns, ...Array(extraManualColumns).fill(' ')]
  const gradeColumnFlex = [gradeCorFlex, ...sizeColumns.map(() => gradeSizeFlex), ...Array(extraManualColumns).fill(gradeExtraFlex)]

  const emptyGradeCells = () => (
    <>
      {sizeColumns.map((size) => (
        <Text key={size} style={{ fontSize: 8, padding: 4, flex: gradeSizeFlex, borderRightWidth: 1, borderRightColor: '#eee' }}>{' '}</Text>
      ))}
      {Array.from({ length: extraManualColumns }).map((_, i) => (
        <Text key={`extra-${i}`} style={{ fontSize: 8, padding: 4, flex: gradeExtraFlex, borderRightWidth: 1, borderRightColor: '#eee' }}>{' '}</Text>
      ))}
    </>
  )

  const consumptionHeaders = ['Tipo', 'Material / Insumo', 'Quantidade']
  const consumptionColumnFlex = [0.7, 3, 1]
  const consumptionTableData =
    data?.consumption?.map((row) => [
      { value: row.kind === 'material' ? 'MP' : 'Insumo' },
      { value: row.name },
      { value: `${formatQuantity(row.quantity)}${row.unit ?? ''}` },
    ]) ?? []

  const observations = data?.fashionProduct?.observations?.trim() ?? ''
  const hasSizeColumns = sizeColumns.length > 0
  const hasNotes = (data?.beneficiamentoNotes?.length ?? 0) > 0
  const hasConsumption = consumptionTableData.length > 0
  const hasProductImage = !!data?.fashionProduct?.coverImageUrl

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderOfPDFFashion opNumber={data?.id} />

        {/* Campos de cabeçalho */}
        <View style={styles.inlineFieldsRow}>
          {pushFields().map((field, index) => (
            <Text key={index} style={styles.inlineField}>
              <Text style={styles.inlineLabel}>{field.label}: </Text>
              {field.value}
            </Text>
          ))}
        </View>

        {/* Grade unificada: quantidades por tamanho + preenchimento manual */}
        {hasSizeColumns && (
          <View style={styles.section}>
            <SectionTitle title="Quantidades por tamanho" />
            <View style={{ flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: '#ccc' }}>
              {gradeHeaders.map((header, i) => (
                <Text
                  key={i}
                  style={{ fontSize: 8, fontWeight: 'bold', textAlign: 'center', padding: 4, flex: gradeColumnFlex[i] ?? 1, borderRightWidth: 1, borderRightColor: '#ccc' }}
                >
                  {header}
                </Text>
              ))}
            </View>
            {/* Linha 1: cor + totais somados de todos os pedidos */}
            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ccc', minHeight: 18 }} wrap={false}>
              <Text style={{ fontSize: 8, padding: 4, flex: gradeCorFlex, borderRightWidth: 1, borderRightColor: '#eee' }}>
                {data?.color?.name ?? '—'}
              </Text>
              {sizeColumns.map((size) => (
                <Text key={size} style={{ fontSize: 8, padding: 4, flex: gradeSizeFlex, textAlign: 'center', borderRightWidth: 1, borderRightColor: '#eee' }}>
                  {totalPerSize[size] ? String(totalPerSize[size]) : '—'}
                </Text>
              ))}
              {Array.from({ length: extraManualColumns }).map((_, i) => (
                <Text key={`extra-total-${i}`} style={{ fontSize: 8, padding: 4, flex: gradeExtraFlex, borderRightWidth: 1, borderRightColor: '#eee' }}>{' '}</Text>
              ))}
            </View>
            {/* Linhas 2–5: retorno para preenchimento manual */}
            {[1, 2, 3, 4].map((label) => (
              <View key={label} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#ccc', minHeight: 18 }} wrap={false}>
                <Text style={{ fontSize: 8, padding: 4, flex: gradeCorFlex, borderRightWidth: 1, borderRightColor: '#eee', color: '#555' }}>
                  Retorno    \    \
                </Text>
                {emptyGradeCells()}
              </View>
            ))}
          </View>
        )}

        {/* Observação */}
        {observations && (
          <View style={styles.section}>
            <SectionTitle title="Observação" />
            <Text style={styles.textBlock}>{observations}</Text>
          </View>
        )}

        {/* Anotações / Beneficiamento */}
        {hasNotes && (
          <View style={styles.section}>
            <SectionTitle title="Anotações" />
            {data?.beneficiamentoNotes?.map((note, index) => {
              const author = (note as any).createdByName || (note as any).CreatedBy?.name || null;
              return (
                <View key={index} style={styles.noteRow}>
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Text style={styles.noteSector}>{note.sectorName}</Text>
                    {author && (
                      <Text style={{ fontSize: 8, color: '#6b7280' }}>Criado por: {author}</Text>
                    )}
                  </View>
                  <Text style={styles.noteText}>{note.description?.trim() || '—'}</Text>
                  {note.imageUrls && note.imageUrls.length > 0 && (
                    <View style={styles.noteImagesRow}>
                      {note.imageUrls.map((url, imgIndex) => (
                        <Image key={imgIndex} src={url} style={styles.noteImage} />
                      ))}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* Consumo de MP e Insumos */}
        {hasConsumption && (
          <View style={styles.section}>
            <SectionTitle title="Consumo de MP e Insumos" />
            <TableOfPDF tableHeaders={consumptionHeaders} tableData={consumptionTableData} columnFlex={consumptionColumnFlex} />
          </View>
        )}

        {/* Imagem do Produto — ao final */}
        {hasProductImage && (
          <View style={styles.productImageSection}>
            <SectionTitle title="Imagem do Produto" />
            <Image src={data!.fashionProduct!.coverImageUrl!} style={styles.productImage} />
          </View>
        )}

        <FooterOfPDFFashion qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={data?.id?.toString()} />
      </Page>
    </Document>
  )
}

export default BodyOfPDFFashion
